import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useIncidentStore } from '@/store/incident.store';
import { useAuthStore } from '@/store/auth.store';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { toast } from '@/utils/toast';
import { ics214Service } from '@/services/ics214.service';
import { api } from '@/services/api';
import type { PaginatedUnitLogs, RosterEntry, ICS214UnitLog } from '@/types/ics214';
import FaIcon from '@/components/common/FaIcon';
import EmptyState from '@/components/common/EmptyState';
import ErrorState from '@/components/common/ErrorState';
import { getErrorMessage, parseApiError } from '@/utils/api-errors';
import LoadingState from '@/components/common/LoadingState';
import Button, { IconButton } from '@/components/common/Button';
import Input from '@/components/common/Input';
import Modal from '@/components/common/Modal';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import MetricValue from '@/components/common/MetricValue';
import StatusDot from '@/components/common/StatusDot';

interface TeamMember {
  user_id: string;
  name: string;
  email: string;
  role_name?: string;
}

export default function FieldTeam() {
  const { activeIncident } = useIncidentStore();
  const { user } = useAuthStore();
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isCreatingLog, setIsCreatingLog] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemoveEntry, setConfirmRemoveEntry] = useState<RosterEntry | null>(null);
  const [pendingMember, setPendingMember] = useState<TeamMember | null>(null);
  const [unitNameInput, setUnitNameInput] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find active unit log for the current incident
  const unitLogParams = useMemo(() => {
    const p: Record<string, string> = { status: 'active', limit: '1' };
    if (activeIncident?.id) p.incident_id = activeIncident.id;
    return p;
  }, [activeIncident?.id]);

  const {
    data: unitLogData,
    isLoading: isLoadingUnitLog,
    isError: unitLogError,
    error: unitLogErrorObj,
    refetch: refetchUnitLog,
  } = useTenantQuery<PaginatedUnitLogs>(
    ['field-team-unitlog', unitLogParams],
    '/ics214',
    { params: unitLogParams, enabled: !!activeIncident?.id }
  );

  const unitLog: ICS214UnitLog | null = unitLogData?.data?.[0] ?? null;
  const unitLogId = unitLog?.id ?? null;

  // Fetch roster for the active unit log
  const {
    data: rosterData,
    isLoading: isLoadingRoster,
    isError: rosterError,
    error: rosterErrorObj,
    refetch: refetchRoster,
  } = useTenantQuery<{ data: RosterEntry[] } | RosterEntry[]>(
    ['field-team-roster', unitLogId],
    `/ics214/${unitLogId}/roster`,
    { enabled: !!unitLogId }
  );

  const roster: RosterEntry[] = useMemo(() => {
    if (!rosterData) return [];
    if (Array.isArray(rosterData)) return rosterData;
    if (rosterData.data && Array.isArray(rosterData.data)) return rosterData.data;
    return [];
  }, [rosterData]);

  // Fetch team members when picker is open
  const { data: teamData } = useTenantQuery<{ data: TeamMember[] } | TeamMember[]>(
    ['team-members'],
    '/team/members',
    { enabled: showPicker }
  );

  const teamMembers: TeamMember[] = useMemo(() => {
    if (!teamData) return [];
    if (Array.isArray(teamData)) return teamData;
    if (teamData.data && Array.isArray(teamData.data)) return teamData.data;
    return [];
  }, [teamData]);

  // IDs of users already on roster
  const rosterUserIds = useMemo(
    () => new Set(roster.filter((r) => r.user_id).map((r) => r.user_id)),
    [roster]
  );

  // Filtered team members
  const filteredMembers = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return teamMembers;
    return teamMembers.filter(
      (m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  }, [teamMembers, search]);

  useEffect(() => {
    if (showPicker) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearch('');
    }
  }, [showPicker]);

  const handleRefresh = () => {
    refetchUnitLog();
    if (unitLogId) refetchRoster();
  };

  const doAddMember = useCallback(
    async (member: TeamMember, logId: string) => {
      setIsAdding(true);
      try {
        await ics214Service.addRosterEntry(logId, {
          person_name: member.name,
          user_id: member.user_id,
          contact_info: member.email,
          check_in_time: new Date().toISOString(),
        });
        toast.success(`${member.name} added to team`);
        await refetchRoster();
        await refetchUnitLog();
      } finally {
        setIsAdding(false);
      }
    },
    [refetchRoster, refetchUnitLog]
  );

  const handleUnitNameConfirm = useCallback(async () => {
    if (!pendingMember || !activeIncident?.id) return;
    const name = unitNameInput.trim() || `${user?.name ?? 'Field'}'s Team`;
    setIsCreatingLog(true);
    try {
      const newLog = await ics214Service.create({
        incident_id: activeIncident.id,
        unit_name: name,
      });
      const created = (newLog as unknown as { data: ICS214UnitLog }).data ?? newLog;
      await refetchUnitLog();
      await doAddMember(pendingMember, created.id);
    } catch (error: unknown) {
      const msg = getErrorMessage(parseApiError(error)) || 'Failed to create team roster';
      toast.error(msg);
    } finally {
      setIsCreatingLog(false);
      setPendingMember(null);
      setUnitNameInput('');
    }
  }, [pendingMember, activeIncident?.id, unitNameInput, user?.name, refetchUnitLog, doAddMember]);

  const handleAddMember = useCallback(
    async (member: TeamMember) => {
      if (isAdding || isCreatingLog) return;

      try {
        if (!unitLogId) {
          if (!activeIncident?.id) {
            toast.error('No active incident selected');
            return;
          }
          // Prompt for unit name before creating
          setUnitNameInput(`${user?.name ?? 'Field'}'s Team`);
          setPendingMember(member);
          return;
        }

        await doAddMember(member, unitLogId);
      } catch (error: unknown) {
        const msg = getErrorMessage(parseApiError(error)) || 'Failed to add team member';
        toast.error(msg);
      }
    },
    [unitLogId, activeIncident?.id, isAdding, isCreatingLog, user?.name, doAddMember]
  );

  const handleCheckIn = async (rosterId: string) => {
    if (!unitLogId) return;
    try {
      await ics214Service.checkIn(unitLogId, rosterId);
      await refetchRoster();
    } catch {
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async (rosterId: string) => {
    if (!unitLogId) return;
    try {
      await ics214Service.checkOut(unitLogId, rosterId);
      await refetchRoster();
    } catch {
      toast.error('Failed to check out');
    }
  };

  const handleRemoveMember = (entry: RosterEntry) => {
    if (!unitLogId || removingId) return;
    setConfirmRemoveEntry(entry);
  };

  const confirmRemove = async () => {
    const entry = confirmRemoveEntry;
    if (!entry || !unitLogId) return;
    setConfirmRemoveEntry(null);
    setRemovingId(entry.id);
    try {
      await ics214Service.removeRosterEntry(unitLogId, entry.id);
      toast.success('Removed from team');
      await refetchRoster();
      await refetchUnitLog();
    } catch {
      toast.error('Failed to remove team member');
    } finally {
      setRemovingId(null);
    }
  };

  // No incident selected
  if (!activeIncident) {
    return (
      <div className="p-4">
        <EmptyState
          variant="operational"
          icon="users"
          title="No incident selected"
          description="Select an incident to manage your field team."
        />
      </div>
    );
  }

  if (isLoadingUnitLog || (unitLogId && isLoadingRoster)) {
    return <LoadingState message="Loading team..." />;
  }

  if (unitLogError || rosterError) {
    return (
      <div className="p-4">
        <ErrorState
          message={getErrorMessage(parseApiError(unitLogError ? unitLogErrorObj : rosterErrorObj))}
          onRetry={() => {
            if (unitLogError) refetchUnitLog();
            if (rosterError) refetchRoster();
          }}
        />
      </div>
    );
  }

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  return (
    <div className="p-4 space-y-3">
      <ConfirmDialog
        isOpen={!!confirmRemoveEntry}
        onClose={() => setConfirmRemoveEntry(null)}
        onConfirm={confirmRemove}
        title="Remove Team Member"
        message={`Remove ${confirmRemoveEntry?.person_name ?? ''} from the team roster?`}
        variant="danger"
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />

      {/* Unit name prompt modal */}
      {!!pendingMember && (
        <Modal
          isOpen={true}
          onClose={() => { setPendingMember(null); setUnitNameInput(''); }}
          title="Name Your Team Roster"
          size="sm"
          closeOnEsc={!isCreatingLog}
          closeOnBackdrop={!isCreatingLog}
          footer={
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => { setPendingMember(null); setUnitNameInput(''); }}
                disabled={isCreatingLog}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUnitNameConfirm}
                disabled={isCreatingLog}
              >
                {isCreatingLog ? 'Creating...' : 'Create & Add'}
              </Button>
            </div>
          }
        >
          <div className="p-6 space-y-4">
            <p className="text-sm text-theme-secondary">
              Enter a name for this ICS-214 unit log before adding {pendingMember.name}.
            </p>
            <Input aria-label="Unit name"
              type="text"
              value={unitNameInput}
              onChange={(e) => setUnitNameInput(e.target.value)}
              placeholder="Unit name"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleUnitNameConfirm(); }}
            />
          </div>
        </Modal>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="heading-3">My Team</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-theme-tertiary">
            <MetricValue value={roster.length} className="font-semibold" /> {roster.length === 1 ? 'member' : 'members'}
          </span>
          <IconButton icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />} label="Refresh team" onClick={handleRefresh} variant="ghost" size="sm" />
          <button
            onClick={() => setShowPicker((prev) => !prev)}
            disabled={isCreatingLog}
            className={`flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] rounded-full touch-manipulation ${
              showPicker
                ? 'bg-gray-300 dark:bg-gray-600 text-theme-secondary active:bg-gray-400'
                : 'bg-primary-600 text-white active:bg-primary-700'
            } disabled:opacity-50`}
            aria-label={showPicker ? 'Close member picker' : 'Add team member'}
            aria-expanded={showPicker}
          >
            <FaIcon
              icon="user-plus"
              className={`h-5 w-5 transition-transform ${showPicker ? 'rotate-45' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Inline User Picker */}
      {showPicker && (
        <div className="bg-surface-primary rounded-lg border border-primary-200 dark:border-primary-800 p-3 space-y-2">
          <Input
            ref={searchInputRef as React.Ref<HTMLInputElement>}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search team members..."
            aria-label="Search team members"
          />

          {isCreatingLog && (
            <p className="text-xs text-theme-tertiary text-center py-2">
              Setting up team roster...
            </p>
          )}

          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {filteredMembers.length === 0 ? (
              <p className="text-sm text-theme-tertiary text-center py-3">
                {search ? 'No matching members found.' : 'No team members available.'}
              </p>
            ) : (
              filteredMembers.map((m) => {
                const alreadyOnRoster = rosterUserIds.has(m.user_id);
                return (
                  <button
                    key={m.user_id}
                    onClick={() => !alreadyOnRoster && handleAddMember(m)}
                    disabled={alreadyOnRoster || isAdding || isCreatingLog}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 min-h-[48px] rounded-lg text-left touch-manipulation transition-colors ${
                      alreadyOnRoster
                        ? 'opacity-50 cursor-default'
                        : 'active:bg-surface-secondary hover:bg-surface-secondary'
                    } disabled:opacity-50`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-theme-primary truncate">
                        {m.name}
                      </p>
                      <p className="text-xs text-theme-tertiary truncate">
                        {m.email}
                      </p>
                    </div>
                    {alreadyOnRoster ? (
                      <FaIcon icon="check" className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <FaIcon icon="plus" className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Roster Cards or Empty State */}
      {roster.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="users"
          title="No team roster yet"
          description="Add a team member to start your field ops roster."
          actionLabel={!showPicker ? 'Add Member' : undefined}
          onAction={!showPicker ? () => setShowPicker(true) : undefined}
        />
      ) : (
        <div className="space-y-2">
          {roster.map((entry) => {
            const isCheckedIn = !!entry.check_in_time && !entry.check_out_time;
            const isCheckedOut = !!entry.check_in_time && !!entry.check_out_time;
            const notCheckedIn = !entry.check_in_time;

            return (
              <div
                key={entry.id}
                className="bg-surface-primary rounded-lg border border-theme-primary p-3 touch-manipulation"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <StatusDot
                        color={isCheckedIn ? 'green' : isCheckedOut ? 'gray' : 'gray'}
                        size="sm"
                        pulse={isCheckedIn}
                      />
                      <p className="text-sm font-medium text-theme-primary truncate">
                        {entry.person_name}
                      </p>
                    </div>
                    {entry.ics_position && (
                      <p className="text-xs text-theme-tertiary mt-0.5 ml-[18px]">
                        {entry.ics_position}
                      </p>
                    )}
                    <p className="text-xs text-theme-tertiary mt-0.5 ml-[18px] tabular-nums">
                      {entry.check_in_time && (
                        <span>In: {formatTime(entry.check_in_time)}</span>
                      )}
                      {entry.check_out_time && (
                        <span> &middot; Out: {formatTime(entry.check_out_time)}</span>
                      )}
                      {entry.contact_info && (
                        <span>
                          {entry.check_in_time ? ' \u00B7 ' : ''}
                          {entry.contact_info}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Check-in / Check-out / Remove buttons */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {notCheckedIn && (
                      <Button
                        onClick={() => handleCheckIn(entry.id)}
                        variant="outline"
                        size="sm"
                      >
                        Check In
                      </Button>
                    )}
                    {isCheckedIn && (
                      <Button
                        onClick={() => handleCheckOut(entry.id)}
                        variant="outline"
                        size="sm"
                      >
                        Check Out
                      </Button>
                    )}
                    {isCheckedOut && (
                      <span className="inline-flex items-center px-2 py-1.5 text-xs text-theme-tertiary">
                        <FaIcon icon="check" className="h-4 w-4" />
                      </span>
                    )}
                    <IconButton
                      icon={<FaIcon icon="trash-can" className="h-3.5 w-3.5" />}
                      label={`Remove ${entry.person_name}`}
                      onClick={() => handleRemoveMember(entry)}
                      disabled={removingId === entry.id}
                      variant="ghost"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
